import React, { Fragment, useContext, useRef, useState, lazy, Suspense } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { DestinationContext } from '../../contexts/DestinationContext';
import Intro from '../Intro';
import ExtraInformation from '../utils/ExtraInformation/ExtraInformation';
import HandPickedSafaris from '../utils/HandpickedSafaris/HandpickedSafaris';
import RecommendedForYou from '../utils/RecommendedForYou/RecommendedForYou';
import Hero from './Hero/Hero';
import QuoteBand from './QuoteBand/QuoteBand';
import GalleryStrip from './GalleryStrip/GalleryStrip';
import SplitCards from './SplitCards/SplitCards';
import FeaturedProperties from './FeaturedProperties/FeaturedProperties';
import SafariIdeas from './SafariIdeas/SafariIdeas';
import MaraGuide from './MaraGuide/MaraGuide';
import TopReasons from './TopReasons/TopReasons';
import BestTimeCalendar from './BestTimeCalendar/BestTimeCalendar';
import LocationSection from './LocationSection/LocationSection';
import GuestVoices from './GuestVoices/GuestVoices';
import FinalCta from './FinalCta/FinalCta';
import StickyCta from './StickyCta/StickyCta';

const LazyAccommodationEnquiryModal = lazy(
  () => import('../utils/AccommodationEnquiryModal/AccommodationEnquiryModal')
);

const routeApi = getRouteApi('/');

const HomePage = () => {
  const destination = useContext(DestinationContext);
  const { countriesList, totalProperties } = routeApi.useLoaderData();
  const plannerRef = useRef(null);
  const accomEnquiryRef = useRef(null);
  const [enquiryModalTarget, setEnquiryModalTarget] = useState(null);

  const openPlanner = (prefill) => plannerRef.current?.open(prefill);
  const openAccommodationEnquiry = (prefill) => {
    setEnquiryModalTarget(prefill);
    accomEnquiryRef.current?.open(prefill?.property, prefill?.intent);
  };

  const scrollToCamps = () =>
    document
      .getElementById('featured-camps')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Fragment>
      {/* Title/description/OG tags are set server-side via the route's
          `head()` (see routes/index.jsx) so they're in the actual HTML
          response, not just patched in after hydration. */}
      <Hero destination={destination} onOpenPlanner={openPlanner} />
      <QuoteBand
        onOpenPlanner={openPlanner}
        totalProperties={totalProperties}
      />
      <RecommendedForYou pageName="homePage" />
      <Intro />
      <GalleryStrip />
      <ExtraInformation
        ref={plannerRef}
        destination={destination?.name}
        howItWorksData={destination?.howItWorks}
        initialCountriesList={countriesList}
        onOpenPlanner={openPlanner}
        onBrowseCamps={scrollToCamps}
      />
      <SplitCards />
      <FeaturedProperties page="home" onOpenPlanner={openAccommodationEnquiry} />
      <HandPickedSafaris currentDestination={destination?.name} />
      <SafariIdeas onOpenPlanner={openPlanner} />
      <MaraGuide />
      <TopReasons />
      <BestTimeCalendar />
      <LocationSection />
      <GuestVoices />
      <FinalCta onOpenPlanner={openPlanner} />
      <StickyCta onOpenPlanner={openPlanner} />
      {enquiryModalTarget && (
        <Suspense fallback={null}>
          <LazyAccommodationEnquiryModal
            ref={accomEnquiryRef}
            isOpen={true}
            initialProperty={enquiryModalTarget.property}
            initialIntent={enquiryModalTarget.intent}
            onClose={() => setEnquiryModalTarget(null)}
          />
        </Suspense>
      )}
    </Fragment>
  );
};

export default HomePage;
